import axios, { AxiosError, AxiosResponse } from "axios";
import {
  AUTH_STORAGE_KEY,
  AUTH_ACCOUNTS,
  CSRFTOKEN,
  AUTH_USER,
} from "@common/constants";
import { AUTH_HEADER, CSRF_HEADER } from "@common/constants/headers";

import API, { reloadToken } from "@services/root/rootApi";
import { sortByAccountName } from "@common/components/Header/utils/utils";
import { Account } from "@services/account/types";
import {
  cleanAuthenticatedUser,
  setIncludedAccounts,
} from "@services/user/hooks/useAuthenticatedUser";

import { PendingSelfRegistrationUser, SelfRegistrationUser } from "./types";
import { CoralogixRum } from "@coralogix/browser";

// THIS WILL BE MOVED TO AUTH SERVICE

// Uses deprecated
export const base64_encode = (str: string) =>
  btoa(unescape(encodeURIComponent(str)));
export const getBase64Token = (
  username: string,
  sessionId: string = "",
  realm: string = "VNOC Maestro"
) =>
  `Symphony ${base64_encode(
    `${base64_encode(username)}:${sessionId}`
  )} realm="${realm}"`;

interface LoginResponse {
  sessionId: string;
  accounts: Account[];
  user: any;
}

type ApiErrorResponseData<T> = {
  errorCode: string;
  error: string;
  message: string;
  errorContext?: T;
};
export type ApiError<T = any> = Omit<AxiosError, "response"> &
  Required<Pick<AxiosError<ApiErrorResponseData<T>>, "response">>;
export type ApiErrorResponse<T = any> = ApiError<T>["response"];

export type LoginMethod = "SAML" | "Legacy";
export const LoginMethods: { [k in LoginMethod]: k } = {
  Legacy: "Legacy",
  SAML: "SAML",
};

const isApiErrorResponse = (
  payload: AxiosResponse | undefined
): payload is ApiErrorResponse =>
  typeof payload !== "undefined" && "status" in payload && "data" in payload;

export const isApiError = (payload: any): payload is ApiError =>
  axios.isAxiosError(payload) && isApiErrorResponse(payload.response);

export const authenticate = async (username: string, password: string) => {
  const csrftoken = await reloadToken(); // safer with brand new token
  const response = await API.post<LoginResponse>(
    `/symphony-api/user/login`,
    {
      username,
      password,
    },
    {
      headers: {
        [AUTH_HEADER]: getBase64Token(username),
        [CSRF_HEADER]: csrftoken,
      },
    }
  );
  return Promise.resolve()
    .then(async () => {
      /**
       * Used to retrieve the JSESSION cookie from the backend as a logged in user.
       * Doing it before any calls as a logged in user is made ensure that there won't be multiple sessions created,
       * and prevent associating x-csrf-tokens with the wrong session id which randomly causes api calls to fail
       */
      return await reloadToken();
    })
    .then(() => {
      const userId = response.data.user?.id;
      localStorage.setItem(CSRFTOKEN, response.headers["x-csrf-token"]);
      localStorage.setItem(
        `${AUTH_ACCOUNTS}_${userId}`,
        JSON.stringify(response.data.accounts.sort(sortByAccountName))
      );
      setIncludedAccounts(response.data.user, response.data.accounts);
      if (__symphony_env__.REACT_CORALOGIX_KEY)
        CoralogixRum.setUserContext({
          user_id: response.data.user.id,
          user_name: `${response.data.user.firstName} ${response.data.user.lastName}`,
          user_email: response.data.user.email,
          user_metadata: {
            role: response.data.user.role.name,
          },
        });
      return localStorage.setItem(
        AUTH_STORAGE_KEY,
        getBase64Token(username, response.data.sessionId)
      );
    })
    .then(() => {
      return response.data;
    });
};

export const forgotPassword = async (username: string) => {
  const csrftoken = await reloadToken(); // safer with brand new token
  const response = await axios.post(
    `${__symphony_env__.REACT_APP_SERVER_URL}/symphony-api/provisioning/user/pwd/forgot`,
    { username },
    {
      headers: {
        [CSRF_HEADER]: csrftoken,
      },
    }
  );

  return response;
};

export const changeForgottenPassword = async (
  email: string,
  newPassword: string,
  token: string
) => {
  const csrftoken = await reloadToken(); // safer with brand new token
  const response = await axios.post(
    `${__symphony_env__.REACT_APP_SERVER_URL}/symphony-api/provisioning/user/pwd/forgot/${token}`,
    { email, newPassword },
    {
      headers: {
        [CSRF_HEADER]: csrftoken,
      },
    }
  );

  return response;
};

export const registerAccount = async (email: string) => {
  const csrftoken = await reloadToken(); // safer with brand new token
  const response = await axios.post(
    `${__symphony_env__.REACT_APP_SERVER_URL}/symphony-api/provisioning/user/registration`,
    { email },
    {
      headers: {
        [CSRF_HEADER]: csrftoken,
      },
    }
  );

  return response;
};

export const getPendingSelfRegistration = async (token: string) => {
  const csrftoken = await reloadToken(); // safer with brand new token
  const response = await axios.get<PendingSelfRegistrationUser>(
    `${__symphony_env__.REACT_APP_SERVER_URL}/symphony-api/provisioning/user/registration/${token}`,
    {
      headers: {
        [CSRF_HEADER]: csrftoken,
      },
    }
  );

  return response;
};

export const completeSelfRegistration = async (
  token: string,
  user: SelfRegistrationUser
) => {
  const csrftoken = await reloadToken(); // safer with brand new token
  const response = await axios.put(
    `${__symphony_env__.REACT_APP_SERVER_URL}/symphony-api/provisioning/user/registration/${token}`,
    user,
    {
      withCredentials: true,
      headers: {
        [CSRF_HEADER]: csrftoken,
      },
    }
  );

  return response;
};

export const logOut = async () => {
  const response = await API.post(`/symphony-api/user/logout`, {});
  cleanAuthenticatedUser();
  return response.data;
};

export const getAccountName = (id: string, userId?: string) => {
  let userKey = userId;
  if (userKey === undefined) {
    userKey = JSON.parse(localStorage.getItem(AUTH_USER) ?? "{}")?.id;
  }
  userKey = userKey ?? "";
  const accounts_key = `${AUTH_ACCOUNTS}_${userKey}`;
  const accounts = JSON.parse(localStorage.getItem(accounts_key) ?? "[]");
  if (accounts) return accounts.find((account: any) => account.id == id)?.name;
  else return "";
};

// If we're logged in (authenticated user present in storage)
// But there's no Symphony token, that means we're logged in through SAML
export const isSAMLSession = () =>
  localStorage.getItem(AUTH_USER) && !localStorage.getItem(AUTH_STORAGE_KEY);
